import { useState } from 'react';
import { isAppError } from '@/shared/api/errors';
import type { SubscriberListRef } from '../api/subscribersApi';
import type { NewPerson } from '../components/AddSubscriberDialog';
import type { ListForm } from '../components/ListDialog';
import { useSubscriberMutations } from './useSubscriberQueries';

export type Naming = { kind: 'new' } | { kind: 'rename'; id: string; name: string; description: string };

export const reasonFor = (error: unknown) => (isAppError(error) ? error.message : 'Could not save. Try again.');

/** What can be done to lists, and the message shown afterwards. onPicked moves the page to a list; ownerId is the
 * RM an admin is managing lists for. */
export function useMyListActions(onPicked: (listId: string) => void, lists: SubscriberListRef[]) {
  const m = useSubscriberMutations();
  const [naming, setNaming] = useState<Naming | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const nameOf = (id: string) => lists.find((l) => l.id === id)?.name ?? 'the list';

  /** One person onto several lists, one call per list; a list that refuses (already on it) is reported, not fatal. */
  const addToLists = async (p: NewPerson) => {
    setAddError(null);
    const results = await Promise.allSettled(p.listIds.map((listId) => m.add.mutateAsync({ listId, name: p.name, phone: p.phone, email: p.email, optedIn: p.optedIn })));
    // A refused name fails on every list and says why; anything else is taken to be a number already on the list.
    const refused = results.find((r) => r.status === 'rejected' && isAppError(r.reason) && r.reason.status === 422);
    if (refused?.status === 'rejected') { setAddError(reasonFor(refused.reason)); return; }
    const failed = p.listIds.filter((_, i) => results[i]?.status === 'rejected');
    if (failed.length === p.listIds.length) { setAddError(`Not added. ${p.name} may already be on ${failed.map(nameOf).join(', ')}.`); return; }
    setAdding(false);
    const added = p.listIds.length - failed.length;
    setToast(failed.length ? `${p.name} added to ${added} of ${p.listIds.length} lists; already on ${failed.map(nameOf).join(', ')}.` : `${p.name} added to ${added === 1 ? nameOf(p.listIds[0] ?? '') : `${added} lists`}.`);
  };

  return {
    m,
    naming,
    adding,
    addError,
    importing,
    toast,
    namingMutation: naming?.kind === 'rename' ? m.renameList : m.createList,
    clearToast: () => { setToast(null); },
    startNew: () => { m.createList.reset(); setNaming({ kind: 'new' }); },
    startRename: (l: SubscriberListRef) => { m.renameList.reset(); setNaming({ kind: 'rename', id: l.id, name: l.name, description: l.description ?? '' }); },
    cancelNaming: () => { setNaming(null); },
    save: (form: ListForm) => {
      const done = (id: string) => { setNaming(null); setToast(`Saved ${form.name}.`); onPicked(id); };
      if (naming?.kind === 'rename') m.renameList.mutate({ id: naming.id, name: form.name, description: form.description }, { onSuccess: (l) => { done(l.id); } });
      else m.createList.mutate(form, { onSuccess: (l) => { done(l.id); } });
    },
    remove: (l: SubscriberListRef) => {
      m.deleteList.mutate(l.id, { onSuccess: () => { onPicked(''); setToast(`${l.name} deleted.`); }, onError: (e) => { setToast(reasonFor(e)); } });
    },
    startAdd: () => { setAddError(null); setAdding(true); },
    cancelAdd: () => { setAdding(false); },
    add: (p: NewPerson) => { void addToLists(p); },
    startImport: () => { m.importClients.reset(); setImporting(true); },
    closeImport: () => { setImporting(false); },
    importCsv: (listId: string, csv: string, optedIn: boolean) => { m.importClients.mutate({ listId, csv, optedIn }); },
    removeMember: (id: string, name: string) => { m.remove.mutate(id, { onSuccess: () => { setToast(`${name} removed.`); }, onError: (e) => { setToast(reasonFor(e)); } }); },
  };
}

export type MyListActions = ReturnType<typeof useMyListActions>;
